import React from "react";
import { Page, Text, StyleSheet, Svg, Line, Rect } from "@react-pdf/renderer";
import { Header } from "../../components/Header";
import Footer from "../../components/Footer";

interface DataPoint {
  vs: number;
  tvd: number;
}

interface Props {
  dataPoints: DataPoint[];
  annotations?: { vs: number; tvd: number; label: string }[];
}

const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  page: {
    padding: "20px",
    fontSize: 14,
    paddingBottom: "50px",
    overflow: "hidden", // Hide overflow content
    height: "100vh", // Set fixed height
    width: "100%",
    textAlign: "center",
    alignContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },
  title: {
    fontSize: 15,
    marginBottom: 10,
    textAlign: "center",
    fontFamily: "Times-Roman",
    fontWeight: 600,
  },
  chartContainer: {
    width: "100%",
    height: 450,
    border: "1pt solid #ccc",
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 60,
    paddingRight: 60,
    marginTop: 20,
    position: "relative",
    alignContent: "center",
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
  },
});

export const PdfTVSChart: React.FC<Props> = ({ dataPoints, annotations }) => {
  if (dataPoints.length === 0) return null;

  const minVS = Math.min(...dataPoints.map((d) => d.vs));
  const maxVS = Math.max(...dataPoints.map((d) => d.vs));
  const minTVD = Math.min(...dataPoints.map((d) => d.tvd));
  const maxTVD = Math.max(...dataPoints.map((d) => d.tvd));

  const width = 700;
  const height = 450;
  const margin = 50;

  const scaleX = (vs: number) =>
    margin + ((vs - minVS) / (maxVS - minVS)) * (width - 2 * margin);
  const scaleY = (tvd: number) =>
    margin + ((tvd - minTVD) / (maxTVD - minTVD)) * (height - 2 * margin - 50); // lower TVD at bottom

  const stepSizeX = 1000;
  const stepSizeY = 500;

  const xTicks = [];
  for (
    let x = Math.ceil(minVS / stepSizeX) * stepSizeX;
    x <= maxVS;
    x += stepSizeX
  ) {
    xTicks.push(x);
  }

  const yTicks = [];
  for (
    let y = Math.ceil(minTVD / stepSizeY) * stepSizeY;
    y <= maxTVD + stepSizeY;
    y += stepSizeY
  ) {
    yTicks.push(y);
  }
  // const legendX = width - margin - 150;
  // const legendY = margin + 10;
  // const legendWidth = 140;
  // const lineHeight = 14;

  return (
    <Page size="A4" orientation="landscape" style={styles.page}>
      <Header showDate={false} />
      <Text style={styles.title}>VS (ft) vs TVD (ftKB)</Text>

      <Svg width={width} height={height} style={styles.chartContainer}>
        {/* Axes */}
        <Line
          x1={margin}
          y1={margin}
          x2={margin}
          y2={height - margin}
          stroke="#000"
          strokeWidth={1}
        />
        <Line
          x1={margin}
          y1={height - margin}
          x2={width - margin}
          y2={height - margin}
          stroke="#000"
          strokeWidth={1}
        />

        {/* X Ticks & Grid */}
        {xTicks.map((x, i) => {
          const xPos = scaleX(x);
          return (
            <React.Fragment key={`xtick-${i}`}>
              {/* Grid line */}
              <Line
                x1={xPos}
                y1={margin}
                x2={xPos}
                y2={height - margin}
                stroke="#ccc"
                strokeWidth={0.5}
              />
              {/* Tick */}
              <Line
                x1={xPos}
                y1={height - margin}
                x2={xPos}
                y2={height - margin + 5}
                stroke="#000"
                strokeWidth={0.5}
              />
              <Text
                x={xPos - 10}
                y={height - margin + 15}
                style={{ fontSize: 8 }}
              >
                {x.toLocaleString()}
              </Text>
            </React.Fragment>
          );
        })}

        {/* Y Ticks & Grid */}
        {yTicks.map((y, i) => {
          const yPos = scaleY(y);
          return (
            <React.Fragment key={`ytick-${i}`}>
              {/* Grid line */}
              <Line
                x1={margin}
                y1={yPos}
                x2={width - margin}
                y2={yPos}
                stroke="#ccc"
                strokeWidth={0.5}
              />
              {/* Tick */}
              <Line
                x1={margin - 5}
                y1={yPos}
                x2={margin}
                y2={yPos}
                stroke="#000"
                strokeWidth={0.5}
              />
              <Text x={margin - 40} y={yPos + 3} style={{ fontSize: 8 }}>
                {y.toLocaleString()}
              </Text>
            </React.Fragment>
          );
        })}

        {/* Axis Labels */}
        <Text
          x={(width + margin) / 2 - 20}
          y={height - margin + 30}
          style={{ fontSize: 10 }}
        >
          VS (ft)
        </Text>
        <Text x={margin - 40} y={margin - 20} style={{ fontSize: 10 }}>
          TVD (ftKB)
        </Text>

        {/* Chart Line */}
        {dataPoints.slice(1).map((point, index) => {
          const prev = dataPoints[index];
          return (
            <Line
              key={index}
              x1={scaleX(prev.vs)}
              y1={scaleY(prev.tvd)}
              x2={scaleX(point.vs)}
              y2={scaleY(point.tvd)}
              stroke="#1976d2"
              strokeWidth={2.5}
            />
          );
        })}
        {/* Annotation Markers */}
        {annotations?.map((a, i) => {
          const x = scaleX(a.vs);
          const y = scaleY(a.tvd);
          // Offset each annotation vertically by 18px per index to avoid overlap
          const labelYOffset = i * -18;
          const labelXOffset = i; // Adjust label position slightly to the right
          return (
            <React.Fragment key={`label-${i}`}>
              <Line
                x1={x - 3}
                y1={y - 3}
                x2={x + 3}
                y2={y + 3}
                stroke={i === 0 ? "red" : i === 1 ? "blue" : "gray"}
                strokeWidth={1.5}
              />
              <Line
                x1={x - 3}
                y1={y + 3}
                x2={x + 3}
                y2={y - 3}
                stroke={i === 0 ? "red" : i === 1 ? "blue" : "gray"}
                strokeWidth={1.5}
              />
              <Rect
                x={x + 5 + labelXOffset}
                y={y + 2 + labelYOffset}
                width={a.label.length * 4}
                height={12}
                fill="#fff"
                stroke={i === 0 ? "red" : i === 1 ? "blue" : "gray"}
                strokeWidth={0.5}
              />
              <Text
                x={x + 8 + labelXOffset}
                y={y + 9 + labelYOffset}
                style={{
                  fontSize: 8,
                  color: i === 0 ? "red" : i === 1 ? "blue" : "gray",
                }}
              >
                {a.label}
              </Text>
            </React.Fragment>
          );
        })}
      </Svg>
      <Footer />
    </Page>
  );
};
